import { io, Socket } from 'socket.io-client';
import type { Message, Conversation } from '../types';

const SOCKET_URL = (import.meta as any).env?.VITE_API_URL || 'https://app.tahc.ai';

export type SocketEventHandler = (...args: any[]) => void;

class SocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  connect(token: string) {
    if (this.socket?.connected) {
      return;
    }

    this.socket = io(SOCKET_URL, {
      auth: {
        token,
      },
      transports: ['websocket', 'polling'],
      reconnectionDelayMax: 10000,
      reconnectionAttempts: this.maxReconnectAttempts,
    });

    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket?.id);
      this.reconnectAttempts = 0;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached');
        this.disconnect();
      }
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  // Conversation events
  joinConversation(conversationId: string) {
    this.socket?.emit('conversation:join', conversationId);
  }

  leaveConversation(conversationId: string) {
    this.socket?.emit('conversation:leave', conversationId);
  }

  sendTypingStart(conversationId: string) {
    this.socket?.emit('typing:start', { conversationId });
  }

  sendTypingStop(conversationId: string) {
    this.socket?.emit('typing:stop', { conversationId });
  }

  updateAgentStatus(status: 'online' | 'away' | 'offline') {
    this.socket?.emit('agent:status', { status });
  }

  approveAISuggestion(messageId: string, conversationId: string) {
    this.socket?.emit('ai:approve', { messageId, conversationId });
  }

  rejectAISuggestion(messageId: string, conversationId: string) {
    this.socket?.emit('ai:reject', { messageId, conversationId });
  }

  takeoverConversation(conversationId: string) {
    this.socket?.emit('takeover:conversation', { conversationId });
  }

  // Event listeners
  onNewMessage(callback: (message: Message) => void) {
    this.socket?.on('message:new', callback);
  }

  onAISuggestion(callback: (data: { message: Message; aiResponse: any }) => void) {
    this.socket?.on('message:ai_suggestion', callback);
  }

  onConversationUpdated(callback: (conversation: Conversation) => void) {
    this.socket?.on('conversation:updated', callback);
  }

  onConversationEscalated(callback: (data: { conversation: Conversation }) => void) {
    this.socket?.on('conversation:escalated', callback);
  }

  onConversationTakenOver(callback: (data: { conversation: Conversation; agent: any }) => void) {
    this.socket?.on('conversation:taken_over', callback);
  }

  onTypingIndicator(callback: (data: { conversationId: string; senderType: string; isTyping: boolean }) => void) {
    this.socket?.on('typing:indicator', callback);
  }

  onAgentStatusChanged(callback: (data: { agentId: string; status: string }) => void) {
    this.socket?.on('agent:status:changed', callback);
  }

  onConversationAssigned(callback: (data: { conversation: Conversation }) => void) {
    this.socket?.on('conversation:assigned', callback);
  }

  onAIAnswering(callback: (data: { conversationId: string }) => void) {
    this.socket?.on('ai:answering', callback);
  }

  // Remove event listeners
  offNewMessage(callback?: SocketEventHandler) {
    this.socket?.off('message:new', callback);
  }

  offAISuggestion(callback?: SocketEventHandler) {
    this.socket?.off('message:ai_suggestion', callback);
  }

  offConversationUpdated(callback?: SocketEventHandler) {
    this.socket?.off('conversation:updated', callback);
  }

  offConversationEscalated(callback?: SocketEventHandler) {
    this.socket?.off('conversation:escalated', callback);
  }

  offConversationTakenOver(callback?: SocketEventHandler) {
    this.socket?.off('conversation:taken_over', callback);
  }

  offTypingIndicator(callback?: SocketEventHandler) {
    this.socket?.off('typing:indicator', callback);
  }

  offAgentStatusChanged(callback?: SocketEventHandler) {
    this.socket?.off('agent:status:changed', callback);
  }

  offConversationAssigned(callback?: SocketEventHandler) {
    this.socket?.off('conversation:assigned', callback);
  }

  offAIAnswering(callback?: SocketEventHandler) {
    this.socket?.off('ai:answering', callback);
  }
}

export const socket = new SocketService();
